import axios from 'axios';
import { BASE_URL, BASE_URL_API, BASE_URL_API_CAL, BASE_URL_API_CAL_EVENT_TYPES, BASE_URL_API_CAL_INVITEES, BASE_URL_API_CAL_SCHEDULED_EVENTS, BASE_URL_API_CAL_USERS, CALENDLY_API_KEY } from '../../constants/constants';
import { clearLocalStorage, getAccesToken, setAccesToken, signOutUser } from '../../Localstorage/localstorage';
import { eventUserColor } from '../../function';
import moment from 'moment';

// Timezone for requests
const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Build headers function to include the access token
const buildHeaders = () => {
  const accessToken = getAccesToken();  // Retrieve access token from storage
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;  // Add access token to Authorization header
  }
  return headers;
};

const buildHeadersCalendly = () => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${CALENDLY_API_KEY}` // Store token securely
  };
  return {headers};
};

// Refresh Access Token Function
const refreshAccessToken = async () => {
  try {
    const response = await axios.post(`${BASE_URL_API}/refresh-token`, {}, { withCredentials: true });
    const { accessToken } = response.data;
    setAccesToken(accessToken); // Update access token in local storage
    return accessToken;
  } catch (error) {
    console.error("Failed to refresh access token:", error);
    throw error;
  }
};

const requestUrlSkipsTokenRefresh = (url) =>
  !url ||
  url.includes('/auth/login') ||
  url.includes('/refresh-token') ||
  url.includes('/auth/signup');

/** Invalid session: clear app storage and go to sign-in (skip on public auth pages). */
const redirectToLoginClearingSession = () => {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (path === '/signin' || path === '/register') return;
  clearLocalStorage();
  signOutUser();
  window.location.href = '/signin';
};

// Axios instance with interceptor for automatic token refresh
const axiosInstance = axios.create();

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const url = originalRequest?.url || '';

    if (status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    if (requestUrlSkipsTokenRefresh(url)) {
      return Promise.reject(error);
    }

    if (!originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const newAccessToken = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error('Refresh token failed. Clearing session and redirecting to login.');
        redirectToLoginClearingSession();
        return Promise.reject(refreshError);
      }
    }

    // Still 401 after a refresh+retry — access token unusable for this API
    redirectToLoginClearingSession();
    return Promise.reject(error);
  }
);

// Axios requests setup
export const AXIOS_WITHOUT_ACCESSTOKEN = (p_url, data) => {
  return axios({
    method: 'post',
    url: `${BASE_URL}/klantenportaal/${p_url}`,
    data: data,
  });
};

export const AXIOS_GET_CAL = async (p_url, params = null) => {
  const options = buildHeadersCalendly();

  // Add params if they exist
  if (params) {
    options.params = params;
  }

  try {
    const response = await axios.get(`${BASE_URL_API_CAL}/${p_url}`, options);
    return response.data;
  } catch (error) {
    console.error('Calendly API Error:', error);
    throw error;
  }
};

export const AXIOS_POST_CAL = async (p_url, data, params = null) => {

  const options = buildHeadersCalendly();

  // Add params if they exist
  if (params) {
    options.params = params;
  }

  try {
    const response = await axios.post(`${BASE_URL_API_CAL}/${p_url}`, data, options);
    return response.data;
  } catch (error) {
    console.error('Calendly API Error:', error);
    throw error;
  }

};

export const AXIOS_GET_CAL_EVENTS = async (p_uid, params = null) => {
  const options = buildHeadersCalendly();

  // Add params if they exist
  if (params) {
    options.params = params;
  }

  try {
    const response = await axios.get(`${BASE_URL_API_CAL_EVENT_TYPES}/${p_uid}`, options);
    return response.data;
  } catch (error) {
    console.error('Calendly API Error:', error);
    throw error;
  }
};

export const AXIOS_POST_CAL_EVENTS = async (p_url, data, params = null) => {
  const options = buildHeadersCalendly();

  // Add params if they exist
  if (params) {
    options.params = params;
  }

  try {
    const response = await axios.post(`${BASE_URL_API_CAL_EVENT_TYPES}/${p_url}`, data, options);
    return response.data;
  } catch (error) {
    console.error('Calendly API Error:', error);
    throw error;
  }

};

export const AXIOS_GET_CAL_USERS = async (p_url, params = null) => {
  const options = buildHeadersCalendly();

  // Add params if they exist
  if (params) {
    options.params = params;
  }

  try {
    const response = await axios.get(`${BASE_URL_API_CAL_USERS}/${p_url}`, options);
    return response.data;
  } catch (error) {
    console.error('Calendly API Error:', error);
    throw error;
  }
};

export const AXIOS_POST_CAL_USERS = async (p_url, data, params = null) => {
  const options = buildHeadersCalendly();

  // Add params if they exist
  if (params) {
    options.params = params;
  }

  try {
    const response = await axios.post(`${BASE_URL_API_CAL_USERS}/${p_url}`, data, options);
    return response.data;
  } catch (error) {
    console.error('Calendly API Error:', error);
    throw error;
  }

};

export const AXIOS_GET_CAL_SCHEDULED_EVENTS = async (params = null) => {
  const options = buildHeadersCalendly(); // Ensure you have your headers set up (e.g., Authorization)
  const { default: pLimit } = await import('p-limit'); // Dynamic import for ESM module


  if (!params) params = {}; // Default params if none provided

  let allEvents = [];
  let nextPageToken = null;

  try {
    // Fetch all paginated results
    do {
      if (nextPageToken) {
        params.page_token = nextPageToken;  // Append next page token to request for further pagination
      } else {
        delete params.page_token;  // Don't include page_token for the first request
      }

      // Add pagination limit count, can be max 100
      params.count = 100;

      // Make the API call using Axios
      const response = await axios.get(BASE_URL_API_CAL_SCHEDULED_EVENTS, { headers: options, params });

      // Collect the events
      const data = response.data;
      if (data?.collection?.length) {
        allEvents.push(...data.collection);
      }

      // Check if there's a next page
      nextPageToken = data.pagination?.next_page_token;  // This is the correct token to continue pagination
      //console.log('Next page token:', nextPageToken); // Debugging pagination

    } while (nextPageToken); // Continue until no more pages are left

    // Clean up params (optional) - You can remove these if you no longer need them
    delete params.sort;
    delete params.count;
    delete params.user;
    delete params.min_start_time;
    delete params.max_start_time;
    delete params.invitee_email;

    // If you have events, limit concurrent invitee requests to 5 at a time
    if (allEvents.length > 0) {
      const limit = pLimit(5);
      const inviteeFetchPromises = allEvents.map(item =>
        limit(async () => {
          const inviteesResponse = await axios.get(`${item.uri}/invitees`, { headers: options });
          const newname = item?.name.trimEnd();

          return {
            ...item,
            cols: moment(item.start_time).day() === 0
              ? "col-start-7"
              : `col-start-${moment(item.start_time).day()}`,
            creators: item.event_memberships,
            invitees: inviteesResponse.data?.collection,
            color: eventUserColor[newname]  // Make sure eventUserColor is properly defined
          };
        })
      );

      // Wait for all invitee requests to complete
      const enrichedEvents = await Promise.all(inviteeFetchPromises);
      return { collection: enrichedEvents };
    }

    return { collection: allEvents };

  } catch (error) {
    console.error('Calendly API Error:', error);
    throw error;
  }
};


export const AXIOS_GET_SCHEDULED_EVENTS = async (p_url, params = null) => {
  const headers = buildHeaders();

  return axiosInstance.get(`${BASE_URL_API}/${p_url}`, { headers });
};


export const AXIOS_POST_CAL_SCHEDULED_EVENTS = async ( data, params = null) => {

  const options = buildHeadersCalendly();

  // Add params if they exist
  if (params) {
    options.params = params;
  }

  try {
    const response = await axios.post(`${BASE_URL_API_CAL_SCHEDULED_EVENTS}`, data, options);
    return response.data;
  } catch (error) {
    console.error('Calendly API Error:', error);
    throw error;
  }

};

export const AXIOS_GET_CAL_INVITEES = async (p_url, params = null) => {
  const options = buildHeadersCalendly();

  // Add params if they exist
  if (params) {
    options.params = params;
  }

  try {
    const response = await axios.get(`${BASE_URL_API_CAL_INVITEES}/${p_url}`, options);
    return response.data;
  } catch (error) {
    console.error('Calendly API Error:', error);
    throw error;
  }
};

export const AXIOS_POST_CAL_INVITEES = async (p_url, data, params = null) => {
  const options = buildHeadersCalendly();

  // Add params if they exist
  if (params) {
    options.params = params;
  }

  try {
    const response = await axios.post(`${BASE_URL_API_CAL}/${p_url}`, data, options);
    return response.data;
  } catch (error) {
    console.error('Calendly API Error:', error);
    throw error;
  }

};

export const AXIOS_GET_LIST_USER_AVAIBLES = async (params=null) => {
  const options = buildHeadersCalendly();

   // Add params if they exist
   if (params) {
    options.params = params;
  }

  try {
    const response = await axios.get(`https://api.calendly.com/user_availability_schedules`, options);
    return response.data;
  } catch (error) {
    console.error('Calendly API Error:', error);
    throw error;
  }

} 

export const AXIOS_GET_EVENT_TYPES = async (params = {
  active: 'true',
  organization: 'https://api.calendly.com/organizations/e5ba7299-d262-4435-acc9-67a68893e28f',
  user: 'https://api.calendly.com/users/b75250d5-32da-42c6-a54c-376aa2afba4a'
}) => {
  const options = buildHeadersCalendly();

  // Add params if they exist
  if (params) {
    options.params = params;
  }

  try {
    const response = await axios.get(`https://api.calendly.com/event_types`, options);
    return response.data; // Return the response data
  } catch (error) {
    console.error('Calendly API Error:', error);
    throw error; // Throw error for handling upstream
  }
};

export const AXIOS_GET_EVENT_TYPES_USERS = async (params = {
  active: 'true',
  organization: 'https://api.calendly.com/organizations/e5ba7299-d262-4435-acc9-67a68893e28f',
  user: 'https://api.calendly.com/users/b75250d5-32da-42c6-a54c-376aa2afba4a'
}) => {
  const options = buildHeadersCalendly();

  // Add params if they exist
  if (params) {
    options.params = params;
  }

  try {
    const response = await axios.get(`https://api.calendly.com/event_types`, options);
    return response.data; // Return the response data
  } catch (error) {
    console.error('Calendly API Error:', error);
    throw error; // Throw error for handling upstream
  }
};

export const AXIOS_GET = async (p_url) => {
  const headers = buildHeaders();
  return axiosInstance.get(`${BASE_URL_API}/${p_url}`, { headers });
};

export const AXIOS_POST = async (p_url, data) => {
  const headers = buildHeaders();
  return axiosInstance.post(`${BASE_URL_API}/${p_url}`, data, { headers });
};

export const AXIOS_DELETE = async (p_url, id, data = {}) => {
  const headers = buildHeaders();
  return axiosInstance.delete(`${BASE_URL_API}/${p_url}/${id}`, {
    headers,
    data: { ...data, timeZone: timezone },
  });
};

export const AXIOS_UPDATE = async (p_url, id, data) => {
  const headers = buildHeaders();
  return axiosInstance.patch(`${BASE_URL_API}/${p_url}/${id}`, { ...data, timeZone: timezone }, { headers });
};

export const AXIOS_UPDATE_MORE = async (p_url, ids, data) => {
  const headers = buildHeaders();
  const idsString = Array.isArray(ids) ? ids.join(',') : ids;
  return axiosInstance.patch(`${BASE_URL_API}/${p_url}/${idsString}`, { ...data, timeZone: timezone }, { headers });
};

export const AXIOS_UPLOAD_IMAGE = async (p_url, id, imageFile) => {
  const headers = buildHeaders();
  const formData = new FormData();
  formData.append('avatar', imageFile);
  return axiosInstance.post(`${BASE_URL_API}/${p_url}/upload/${id}`, formData, {
    headers: {
      ...headers,
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const AXIOS_UPLOAD_DOCUMENTS = async (p_url, formData) => {
  const headers = buildHeaders();
  return axiosInstance.post(`${BASE_URL_API}/${p_url}`, formData, {
    headers: {
      ...headers,
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const AXIOS_UPLOAD_DOCUMENT = async (p_url, formData) => {
  const headers = buildHeaders();
  return axiosInstance.post(`${BASE_URL_API}/${p_url}`, formData, {
    headers: {
      ...headers,
      'Content-Type': 'multipart/form-data',
    },
  });
};
